/**
 * Maps raw tool names to human-readable progress labels and, when args are
 * present, embeds the semantically-primary argument so multiple calls of the
 * same tool visually disambiguate. E.g.:
 *   recall_data_findings({query: "asthma linkage"})
 *     → "Recalling prior findings for \"asthma linkage\""
 *   explore_schema({intent: "adults with severe asthma"})
 *     → "Exploring schema for \"adults with severe asthma\""
 *
 * analytical variable identifier convention: user-facing copy refers to the analytical variable's `slug` as
 * its "name" — the on-wire field stays `slug` for compatibility, but every
 * label routed through this map talks about names. Backend-only artifact
 * paths (`avs/<slug>/draft.sql`, `manifest.json`, `explain.md`) are
 * extracted to the bare slug via {@link extractAvNameFromPath} so the
 * card never leaks repository-internal paths to the epi.
 */

/**
 * Pull the analytical variable name (slug) out of a backend artifact path. Returns the
 * input unchanged when the input doesn't look like a path. Examples:
 *   `avs/severe_asthma_index_date/draft.sql` → `severe_asthma_index_date`
 *   `avs/bmi_at_index_12m/manifest.json`     → `bmi_at_index_12m`
 *   `severe_asthma_index_date`               → `severe_asthma_index_date` (no change)
 */
export const extractAvNameFromPath = (raw: string): string => {
  const match = /^avs\/([^/]+)\/(draft\.sql|manifest\.json|explain\.md)$/.exec(raw);
  return match ? match[1] : raw;
};

/** Returns true when the value is one of the backend artifact paths we hide. */
export const isInternalArtifactPath = (value: unknown): boolean =>
  typeof value === 'string' && /^avs\/[^/]+\/(draft\.sql|manifest\.json|explain\.md)$/.test(value);

interface LabelSpec {
  base: string;
  /** Arg key whose value best describes why this call is happening. */
  primaryArg?: string;
  /** Template `{value}` gets replaced with the primary arg's value. */
  withContext?: (value: string) => string;
  /**
   * When set, the primary-arg value is rewritten through this transform
   * before being interpolated. Used to convert `avs/<slug>/draft.sql` to
   * just the slug so the visible label reads as a name.
   */
  transformValue?: (value: string) => string;
  /**
   * Full control over the label from the whole arg map — used when a single
   * `primaryArg` can't express the row (e.g. a search that may be by name OR by
   * member concept OR by domain, and we want to show whichever the model
   * actually used). Tried BEFORE `primaryArg`; return `null` to fall through to
   * `primaryArg`/`base`. Like `primaryArg`, this is curated researcher-facing
   * context, so it is allowed even under `hideInternals`.
   */
  describe?: (args: Record<string, unknown>) => string | null;
}

const SPECS: Record<string, LabelSpec> = {
  recall_data_findings: {
    base: 'Recalling prior findings',
    primaryArg: 'query',
    withContext: (v) => `Recalling prior findings for “${v}”`,
  },
  recall_knowledge: {
    base: 'Recalling knowledge',
    primaryArg: 'query',
    withContext: (v) => `Recalling knowledge for “${v}”`,
  },
  remember_knowledge: {
    base: 'Saving to knowledge store',
    primaryArg: 'topic',
    withContext: (v) => `Saving to knowledge store: ${v}`,
  },
  explore_schema: {
    base: 'Exploring warehouse schema',
    primaryArg: 'intent',
    withContext: (v) => `Exploring schema for “${v}”`,
  },
  resolve_concepts: {
    base: 'Resolving clinical concepts',
    primaryArg: 'clinical_terms',
    withContext: (v) => `Resolving clinical concepts: ${v}`,
  },
  draft_sql: {
    base: 'Drafting SQL',
    primaryArg: 'study_spec_summary',
    withContext: (v) => `Drafting SQL: ${v}`,
  },
  validate_sql: {
    base: 'Checking the variable',
    primaryArg: 'sql_or_path',
    transformValue: extractAvNameFromPath,
    withContext: (v) => `Checking ${v}`,
  },
  // verify_analytical_variable is the audit-tracked verification flow. The `slug` arg is the
  // analytical variable name; do NOT show paths here — `slug_or_path` resolution happens
  // server-side. Naming-wise: epi sees "Verifying <name>", never "<slug>".
  verify_analytical_variable: {
    base: 'Verifying variable',
    primaryArg: 'slug',
    withContext: (v) => `Verifying ${v}`,
  },
  // SQL Validator sub-tools — these run beneath verify_analytical_variable/validate_sql and
  // accept a `sql_or_path` arg whose value is a backend-only path
  // (`avs/<name>/draft.sql`). We extract the name so the visible row reads
  // "Sampling rows for <name>" instead of leaking the artifact path.
  parse_sql: {
    base: 'Parsing SQL',
    primaryArg: 'sql_or_path',
    transformValue: extractAvNameFromPath,
    withContext: (v) => `Parsing SQL for ${v}`,
  },
  lint_sql: {
    base: 'Reviewing house rules',
    primaryArg: 'sql_or_path',
    transformValue: extractAvNameFromPath,
    withContext: (v) => `Reviewing house rules for ${v}`,
  },
  dry_run_sample: {
    base: 'Pulling a sample',
    primaryArg: 'sql_or_path',
    transformValue: extractAvNameFromPath,
    withContext: (v) => `Pulling sample for ${v}`,
  },
  // Generic attrition tool — walks the CTE chain of any SQL counting
  // DISTINCT person_id per step. Works whether the SQL is an analytical variable (per-CTE
  // drop-off in one variable), a cohort definition (full I/E funnel), or
  // an ad-hoc query.
  attrition_funnel: {
    base: 'Walking the attrition funnel',
    primaryArg: 'sql_or_path',
    transformValue: extractAvNameFromPath,
    withContext: (v) => `Walking attrition for ${v}`,
  },
  // Legacy alias mapping — old `cohort_attrition` tool name renders the
  // same way as `attrition_funnel` if it ever shows up in a session
  // history captured before the rename.
  cohort_attrition: {
    base: 'Walking the attrition funnel',
    primaryArg: 'sql_or_path',
    transformValue: extractAvNameFromPath,
    withContext: (v) => `Walking attrition for ${v}`,
  },
  estimate_full_cost: {
    base: 'Estimating cost',
    primaryArg: 'sql_or_path',
    transformValue: extractAvNameFromPath,
    withContext: (v) => `Estimating cost for ${v}`,
  },
  // analytical variable Drafter — `slug` is the analytical variable name we surface; everything else is
  // implementation detail.
  draft_analytical_variable: {
    base: 'Drafting variable',
    primaryArg: 'slug',
    withContext: (v) => `Drafting ${v}`,
  },
  submit_analytical_variable: {
    base: 'Finalizing variable draft',
    primaryArg: 'slug',
    withContext: (v) => `Finalizing ${v}`,
  },
  draft_composition: {
    base: 'Drafting composition',
    primaryArg: 'slug',
    withContext: (v) => `Composing ${v}`,
  },
  explain_sql: {
    base: 'Explaining SQL',
    primaryArg: 'slug',
    withContext: (v) => `Explaining ${v}`,
  },
  // Distribution / coverage probes the orchestrator fires on its own
  // (separate from verify_analytical_variable's substeps).
  compute_analytical_variable_distribution: {
    base: 'Computing distribution',
    primaryArg: 'slug',
    withContext: (v) => `Computing distribution for ${v}`,
  },
  // Session / artifact catalog. These are cheap reads — the friendly label
  // exists so the row reads as English, not as a tool ID.
  list_session_variables: {
    base: 'Checking what exists in this session',
  },
  get_analytical_variable: {
    base: 'Reading variable',
    primaryArg: 'slug',
    withContext: (v) => `Reading ${v}`,
  },
  list_datasets: {
    base: 'Listing available datasets',
  },
  describe_dataset: {
    base: 'Describing dataset',
    primaryArg: 'name',
    withContext: (v) => `Describing dataset ${v}`,
  },
  // Checkpoint / oversight controls. The card surfaces the stage so the
  // epi can see at a glance what gate is being raised.
  checkpoint: {
    base: 'Pausing for review',
    primaryArg: 'stage',
    withContext: (v) => `Pausing at ${v}`,
  },
  // Knowledge store reads/writes. Hide the raw id; the row already says
  // "knowledge".
  get_knowledge: {
    base: 'Reading knowledge entry',
  },
  promote_analytical_variable: {
    base: 'Promoting variable',
    primaryArg: 'slug',
    withContext: (v) => `Promoting ${v}`,
  },
  // Memory tools (the per-user pinned-memory store, distinct from
  // knowledge).
  list_memories: {
    base: 'Listing memories',
  },
  propose_memory: {
    base: 'Proposing a new memory',
    primaryArg: 'topic',
    withContext: (v) => `Proposing memory: ${v}`,
  },
  save_memory: {
    base: 'Saving to memory',
    primaryArg: 'text',
    withContext: (v) => `Saving to memory: ${v}`,
  },
  propose_forget_memory: {
    base: 'Proposing memory removal',
  },
  // Concept Group Proposer sub-agent tools.
  search_omop_concepts: {
    base: 'Searching OMOP concepts',
    primaryArg: 'query',
    withContext: (v) => `Searching OMOP for "${v}"`,
  },
  fetch_concept_descendants: {
    base: 'Fetching concept descendants',
    primaryArg: 'concept_id',
    withContext: (v) => `Fetching descendants of ${v}`,
  },
  explore_concept_hierarchy: {
    base: 'Exploring concept hierarchy',
    primaryArg: 'concept_id',
    withContext: (v) => `Exploring hierarchy of ${v}`,
  },
  finalize_concept_group_proposal: {
    base: 'Finalizing concept-group proposal',
    primaryArg: 'term',
    withContext: (v) => `Finalizing concept-group proposal for "${v}"`,
  },
  // Template tools at the AVDrafter layer. Internal-mechanic naming —
  // these never advertise "template" terminology to the epi (the
  // orchestrator prompt forbids it). The card label here is for the
  // collapsed-by-default tool trail, where epi-friendly framing matters
  // less than disambiguating same-tool calls; we still avoid the word
  // "template" in the headline.
  get_template: {
    base: 'Loading SQL skeleton',
    primaryArg: 'template_id',
    withContext: (v) => `Loading skeleton ${v}`,
  },
  remember_data_finding: {
    base: 'Saving finding to memory',
    primaryArg: 'topic',
    withContext: (v) => `Saving finding: ${v}`,
  },
  log_gap_event: {
    base: 'Logging product feedback',
    primaryArg: 'category',
    withContext: (v) => `Logging gap: ${v}`,
  },
  ask_clarifying_question: {
    base: 'Asking a clarifying question',
  },
  propose_codeset: {
    base: 'Proposing concept group',
    primaryArg: 'term',
    withContext: (v) => `Proposing concept group for “${v}”`,
  },
  select_concept_groups: {
    base: 'Selecting concept groups',
    primaryArg: 'terms',
    withContext: (v) => `Selecting concept groups: ${v}`,
  },
  // OR-6124: the agentic search sub-agent delegate (flexible library discovery).
  find_concept_group: {
    base: 'Finding concept group',
    primaryArg: 'term',
    withContext: (v) => `Finding concept group for “${v}”`,
  },
  // OR-6124: the agentic search sub-agent's own catalog probes, surfaced as
  // nested rows under `find_concept_group`. The row shows WHAT was searched —
  // by name, or by member concept, or by domain/owner — so a long stack of
  // otherwise-identical "Search Concept Catalog" rows disambiguates.
  search_concept_catalog: {
    base: 'Searching concept catalog',
    describe: (args) => {
      const name = stringifyValue(args.name_pattern);
      if (name) return `Searching concept catalog for “${name}”`;
      const member = stringifyValue(args.member_concept_name ?? args.member_concept_code);
      if (member) return `Searching concept catalog by member “${member}”`;
      const domain = stringifyValue(args.domain);
      if (domain) return `Searching concept catalog in ${domain}`;
      const owner = stringifyValue(args.owner);
      if (owner) return `Searching concept catalog by owner ${owner}`;
      return null;
    },
  },
  // OR-6124: inline OMOP lookup (demographics / tiny fixed sets).
  lookup_omop_concept: {
    base: 'Looking up OMOP concept',
    primaryArg: 'term',
    withContext: (v) => `Looking up OMOP concept “${v}”`,
  },
  propose_concept_group: {
    base: 'Proposing concept group',
    primaryArg: 'term',
    withContext: (v) => `Proposing concept group for “${v}”`,
  },
  // Schema Explorer sub-agent tools — surfaced as nested sub-calls under
  // `explore_schema`. Labels embed the semantically-primary argument so
  // multiple catalog probes on the same turn disambiguate visually.
  search_catalog: {
    base: 'Searching catalog',
    primaryArg: 'keyword',
    withContext: (v) => `Searching catalog for “${v}”`,
  },
  list_schemas: {
    base: 'Listing schemas',
    primaryArg: 'database',
    withContext: (v) => `Listing schemas in ${v}`,
  },
  list_tables: {
    base: 'Listing tables',
    primaryArg: 'schema_name',
    withContext: (v) => `Listing tables in ${v}`,
  },
  describe_table: {
    base: 'Describing table',
    primaryArg: 'table',
    withContext: (v) => `Describing ${v}`,
  },
  sample_rows: {
    base: 'Sampling rows',
    primaryArg: 'table',
    withContext: (v) => `Sampling rows from ${v}`,
  },
  column_profile: {
    base: 'Profiling column',
    primaryArg: 'column',
    withContext: (v) => `Profiling column ${v}`,
  },
  top_k_values: {
    base: 'Inspecting top values',
    primaryArg: 'column',
    withContext: (v) => `Top values in ${v}`,
  },
  run_sql_readonly: {
    base: 'Running read-only SQL',
    primaryArg: 'sql',
    withContext: (v) => `Running SQL: ${v}`,
  },
  // Concept Group Selector / Proposer sub-agent tools.
  preview_concept_group: {
    base: 'Previewing concept group',
    primaryArg: 'concept_group_id',
    withContext: (v) => `Previewing concept group ${v}`,
  },
  count_descendants: {
    base: 'Counting descendants',
    primaryArg: 'concept_id',
    withContext: (v) => `Counting descendants of ${v}`,
  },
  check_definition_prevalence: {
    base: 'Checking prevalence',
    primaryArg: 'term',
    withContext: (v) => `Checking prevalence of ${v}`,
  },
  finalize_concept_group: {
    base: 'Finalizing concept group',
    primaryArg: 'term',
    withContext: (v) => `Finalizing concept group for “${v}”`,
  },
  // SQL Drafter / Validator sub-agent tools.
  list_templates: {
    base: 'Listing SQL templates',
  },
  freeform_sql: {
    base: 'Drafting free-form SQL',
    primaryArg: 'reason',
    withContext: (v) => `Drafting free-form SQL (${v})`,
  },
  fill_template: {
    base: 'Filling SQL template',
    primaryArg: 'template_id',
    withContext: (v) => `Filling template ${v}`,
  },
  list_probe_templates: {
    base: 'Listing probe templates',
  },
  run_probe: {
    base: 'Running probe',
    primaryArg: 'template_id',
    withContext: (v) => `Running probe ${v}`,
  },
  // "query", not "SQL" — researcher-facing wording (PR #4118 review).
  execute_sql: {
    base: 'Executing query',
    primaryArg: 'reason',
    withContext: (v) => `Executing query (${v})`,
  },
  // OR-6328 label sweep — every tool the orchestrator or a sub-agent can
  // register gets a curated researcher-language entry so the working-state
  // row never falls back to `Title Case Name: <first arg value>` (which can
  // put raw SQL or artifact paths in the label). Weave a primary argument
  // only where that argument is researcher-readable prose or a name — never
  // SQL text, ids, or paths.
  //
  // Scoped ad-hoc SQL (ADR-0007). `query` is raw SQL — base label only.
  run_scoped_sql: {
    base: 'Executing query',
  },
  // Analytical-variable library reads. `name`/`slug` is the variable's
  // user-facing name per the naming convention at the top of this file.
  get_analytical_variable_sql: {
    base: 'Reading variable SQL',
    primaryArg: 'name',
    withContext: (v) => `Reading the SQL for ${v}`,
  },
  get_analytical_variable_by_name: {
    base: 'Reading variable',
    primaryArg: 'name',
    withContext: (v) => `Reading ${v}`,
  },
  get_analytical_variable_definition: {
    base: 'Reading variable definition',
  },
  search_analytical_variable_library: {
    base: 'Searching the variable library',
    primaryArg: 'query',
    withContext: (v) => `Searching the variable library for “${v}”`,
  },
  search_analytical_variables_by_query: {
    base: 'Searching the variable library',
    primaryArg: 'query',
    withContext: (v) => `Searching the variable library for “${v}”`,
  },
  // Analytical-variable draft workflow.
  create_analytical_variable_draft: {
    base: 'Drafting variable',
    primaryArg: 'name',
    withContext: (v) => `Drafting ${v}`,
  },
  get_analytical_variable_draft: {
    base: 'Reading the variable draft',
  },
  update_analytical_variable_draft: {
    base: 'Updating the variable draft',
  },
  save_analytical_variable_draft: {
    base: 'Saving the variable draft',
  },
  save_analytical_variable: {
    base: 'Saving variable',
    primaryArg: 'name',
    withContext: (v) => `Saving ${v}`,
  },
  draft_analytical_variable_sql: {
    base: 'Drafting the variable SQL',
  },
  verify_analytical_variable_draft: {
    base: 'Verifying variable',
  },
  submit_draft_for_review: {
    base: 'Submitting the draft for review',
  },
  update_analytical_variable_concept_mappings: {
    base: 'Updating concept mappings',
  },
  process_analytical_variable: {
    base: 'Processing variable',
  },
  check_analytical_variable_editable: {
    base: 'Checking the variable can be edited',
  },
  check_cohort_variable_coverage: {
    base: 'Checking variable coverage',
  },
  check_population_count: {
    base: 'Checking population count',
  },
  audit_analytical_variable: {
    base: 'Auditing variable',
    primaryArg: 'slug',
    withContext: (v) => `Auditing ${v}`,
  },
  read_audit_report: {
    base: 'Reading the audit report',
  },
  propose_analytical_variable_checkpoint: {
    base: 'Pausing for review',
  },
  // Concept-group workflow.
  select_concept_group: {
    base: 'Selecting concept groups',
  },
  search_concept_groups: {
    base: 'Searching concept groups',
  },
  plan_concept_terms: {
    base: 'Planning concept searches',
  },
  create_concept_group_draft: {
    base: 'Drafting concept group',
    primaryArg: 'name',
    withContext: (v) => `Drafting concept group ${v}`,
  },
  update_concept_group_draft: {
    base: 'Updating the concept group draft',
  },
  refine_concept_group_draft: {
    base: 'Refining the concept group',
  },
  discard_concept_group_draft: {
    base: 'Discarding the concept group draft',
  },
  get_concept_group_definition: {
    base: 'Reading concept group definition',
  },
  get_concept_group_draft_state: {
    base: 'Checking the concept group draft',
  },
  use_existing_concept_group: {
    base: 'Using an existing concept group',
    primaryArg: 'concept_group_name',
    withContext: (v) => `Using concept group ${v}`,
  },
  finalize_selection_no_match: {
    base: 'Recording that no concept group matched',
  },
  // Attached-file tool (OR-6486). Curated so the uncurated fallback doesn't put
  // the server-minted `file_id` uuid in the row.
  create_concept_groups_from_file: {
    base: 'Creating concept groups from the file',
  },
  // Display-only steps reported inside `create_concept_groups_from_file`'s row;
  // named in `concept_group_bulk_upload.py`, not callable by the model.
  read_attached_file: {
    base: 'Reading the attached file',
    primaryArg: 'filename',
    withContext: (v) => `Reading attached file ${v}`,
  },
  plan_code_mapping: {
    base: 'Checking which lists need code mapping',
    describe: (args) => {
      const mapping = typeof args.mapping === 'number' ? args.mapping : null;
      const total = typeof args.total === 'number' ? args.total : null;
      if (mapping === null || total === null) return null;
      if (mapping === 0) return 'No code mapping needed — every list already holds standard codes';
      return `${mapping} of ${total} lists need mapping to standard codes`;
    },
  },
  map_concept_codes: {
    base: 'Mapping codes to standard concepts',
    primaryArg: 'name',
    withContext: (v) => `Mapping codes for ${v}`,
    describe: (args) => {
      const name = stringifyValue(args.name);
      const codes = typeof args.codes === 'number' ? args.codes : 0;
      return name && codes > 0 ? `Mapping ${codes} codes for ${name}` : null;
    },
  },
  create_concept_group_from_list: {
    base: 'Creating a concept group',
    primaryArg: 'name',
    withContext: (v) => `Creating concept group ${v}`,
  },
  // Cohort / dataset reads.
  get_cohort_definition: {
    base: 'Reading the cohort definition',
  },
  get_cohort_results_summary: {
    base: 'Reading cohort results',
  },
  get_dataset_study_period: {
    base: 'Checking the dataset study period',
  },
  list_data_checks: {
    base: 'Listing data checks',
  },
  list_project_assets: {
    base: 'Reviewing project assets',
  },
  // Conversation recall.
  search_chat_history: {
    base: 'Reviewing earlier conversation',
    primaryArg: 'query',
    withContext: (v) => `Reviewing earlier conversation about “${v}”`,
  },
  get_chat_history_entry: {
    base: 'Reviewing earlier conversation',
  },
  // Memory / SQL-review / control tools.
  cite_memory: {
    base: 'Citing a memory',
  },
  judge_sql: {
    base: 'Reviewing the SQL',
  },
  set_task_scope: {
    base: 'Focusing on a task',
  },
  // Cohort-composition tools (tools/cohort_composition.py — registered via
  // bare ``@tool``, so the function name IS the tool name; the earlier
  // ``@tool(name=...)`` sweep missed them). Base labels only: their
  // descriptive args (``cohort_description`` etc.) are long prose that
  // belongs in the expandable detail, not the row label.
  draft_cohort: {
    base: 'Drafting the cohort',
  },
  create_cohort: {
    base: 'Creating the cohort',
  },
  explain_cohort: {
    base: 'Explaining the cohort',
  },
  process_cohort: {
    base: 'Processing the cohort',
  },
  check_cohort_editable: {
    base: 'Checking the cohort can be edited',
  },
  list_cohort_pending_criteria: {
    base: 'Reviewing remaining cohort criteria',
  },
  add_cohort_criterion: {
    base: 'Adding a cohort criterion',
  },
  remove_criterion_from_cohort: {
    base: 'Removing a cohort criterion',
  },
  add_analytical_variable_to_cohort: {
    base: 'Adding a variable to the cohort',
  },
  remove_analytical_variable_from_cohort: {
    base: 'Removing a variable from the cohort',
  },
  replace_analytical_variable_in_cohort: {
    base: 'Replacing a variable in the cohort',
  },
  apply_cohort_plan_edits: {
    base: 'Updating the cohort plan',
  },
  commit_cohort_plan: {
    base: 'Saving the cohort plan',
  },
  set_calendar_date_index: {
    base: 'Setting the index date',
  },
  update_cohort_study_period: {
    base: 'Updating the study period',
  },
};

// When we've never seen the tool before, surface the most semantically-likely
// argument inline so the row still reads like a sentence instead of the tool's
// raw identifier. Order matters: earlier keys win.
const GENERIC_PRIMARY_KEYS = [
  'query',
  'keyword',
  'intent',
  'term',
  'topic',
  'question',
  'table',
  'column',
  'database',
  'schema_name',
  'template_id',
  'concept_group_id',
  'concept_id',
  'reason',
  'sql',
];

const MAX_INLINE_VALUE_LEN = 80;

const stringifyValue = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  let str: string;
  if (typeof value === 'string') str = value;
  else if (Array.isArray(value)) {
    if (value.length === 0) return null;
    str = value
      .slice(0, 4)
      .map((v) => (typeof v === 'string' ? v : JSON.stringify(v)))
      .join(', ');
    if (value.length > 4) str += `, +${value.length - 4}`;
  } else str = JSON.stringify(value);
  return str.length > MAX_INLINE_VALUE_LEN ? `${str.slice(0, MAX_INLINE_VALUE_LEN)}…` : str;
};

const titleCase = (toolName: string): string =>
  toolName
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

// Per-tool hint to surface alongside the running tool-call row. Only set
// for tools that routinely take longer than the user expects, so the row
// communicates "still working, this is normal" instead of looking stuck.
// Verification fans out into multiple full-table scans on the active
// dataset's CONDITION_OCCURRENCE / OBSERVATION tables; on a Small
// warehouse against a multi-million-patient registry that's a few minutes,
// not a few seconds.
const SLOW_TOOL_HINTS: Record<string, string> = {
  verify_analytical_variable:
    'This can take a few minutes on large registries. The Verification tab fills in as sections complete.',
};

export const slowToolHint = (toolName: string): string | null => SLOW_TOOL_HINTS[toolName] ?? null;

export interface FriendlyToolLabelOptions {
  /**
   * OR-6328 (``research_assistant_hide_tool_internals``): tool internals are
   * hidden from the researcher, so a tool with no curated ``SPECS`` entry
   * must render its base label ONLY — never the generic first-argument
   * suffix, which can carry raw SQL, artifact paths, or result-shaped
   * payloads. Curated entries keep their ``primaryArg`` weaving: those
   * argument choices are deliberate, researcher-facing context (the ticket
   * itself asks for "Recalling knowledge for {}").
   * TODO: cleanup with research_assistant_hide_tool_internals ff
   */
  hideInternals?: boolean;
}

export const friendlyToolLabel = (
  toolName: string,
  args?: Record<string, unknown>,
  options?: FriendlyToolLabelOptions
): string => {
  const spec = SPECS[toolName];
  if (!spec) {
    const base = titleCase(toolName);
    if (options?.hideInternals) return base;
    if (args) {
      for (const key of GENERIC_PRIMARY_KEYS) {
        const value = stringifyValue(args[key]);
        if (value) return `${base}: ${value}`;
      }
      // Fallback: first non-empty string-like value, whatever key it lives under.
      for (const [, v] of Object.entries(args)) {
        const value = stringifyValue(v);
        if (value) return `${base}: ${value}`;
      }
    }
    return base;
  }
  if (args && spec.describe) {
    const described = spec.describe(args);
    if (described) return described;
  }
  if (args && spec.primaryArg && spec.withContext) {
    const raw = args[spec.primaryArg];
    const formatted = stringifyValue(raw);
    if (formatted) {
      const transformed = spec.transformValue ? spec.transformValue(formatted) : formatted;
      return spec.withContext(transformed);
    }
  }
  return spec.base;
};
